<template>
  <div :class="rootClasses" :style="rootStyles">
    <ElementControls
      v-if="editing"
      :no-page-break="!pageBreakAllowed"
      @delete="$emit('delete')"
      @duplicate="$emit('duplicate')"
      @page-break="$emit('page-break')"
      @hide="$emit('hide')"
    />
    <AnalyticsWrapper>
      <div :class="layoutClasses">
        <div
          v-if="label || description"
          class="formester-label-description-wrapper"
          :style="labelDescriptionWrapperStyles"
        >
          <label
            :class="labelClasses"
            :style="labelStyles"
            :for="item.id"
            v-html="label"
          />
          <SmartText
            :active="active"
            :editing="editing"
            :item="item"
            :style="descriptionStyles"
            :text="description"
          />
        </div>

        <div class="select-arrow">
          <i class="fa fa-chevron-down" aria-hidden="true" />
        </div>
        <select
          :id="item.id"
          v-model="itemValue"
          :data-uuid="item.id"
          :class="inputClasses"
          :style="[inputStyles, fieldStyles]"
          :name="item.label"
          :required="item.required"
          :disabled="isEditingDisabled || editing || isDisabled"
          @keydown="chooseOption"
          @change="dropdownSelect"
          @focus="handleFocus(item)"
          @blur="handleBlur(item)"
        >
          <option
            v-for="option in shuffledOptions"
            :key="option.id"
            class="dropdown-option"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </select>

        <div v-if="revealAnswer" :style="revealAnswerStyle" class="mt-2">
          <b>Correct answer:</b> {{ correctAnswerLabels.join(", ") }}
        </div>

        <LockIndicator :item="item" />
        <ErrorMessage :item="item" />

        <QuizFeedback
          v-if="revealAnswer"
          class="mt-3"
          :type="quizFeedbackType"
          :message="quizFeedbackMessage"
        />
      </div>
    </AnalyticsWrapper>
  </div>
</template>

<script>
import ElementControls from "@/components/form_fields/shared/element-controls.vue";
import commonStyles from "@/components/form_fields/shared/commonStyles";
import sharedActions from "@/components/form_fields/shared/sharedActions";
import ErrorMessage from "@/components/form_fields/shared/error-message.vue";
import { set as setItem } from "lodash-es";
import SmartText from "@/components/form_fields/shared/smart-text.vue";
import AnalyticsWrapper from "@/components/form_fields/shared/analytics-wrapper.vue";
import QuizFeedback from "@/components/form_fields/shared/_quiz-feedback.vue";

export default {
  name: "Dropdown",
  components: {
    ElementControls,
    ErrorMessage,
    SmartText,
    AnalyticsWrapper,
    QuizFeedback,
  },
  mixins: [commonStyles, sharedActions],
  props: {
    item: {
      type: Object,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    styling: {
      type: Object,
      required: true,
    },
    pageBreakAllowed: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["delete", "page-break", "duplicate", "hide"],
  data() {
    return {
      arrowPressed: false,
    };
  },
  computed: {
    itemValue: {
      get() {
        return this.item.value;
      },
      set(e) {
        setItem(this.item, "value", e);
      },
    },
    inputClasses() {
      return {
        "formester-input": true,
        "formester-input__dropdown": true,
      };
    },
    inputStyles() {
      return {
        "font-size": `${this.styling.answerFontSize}px` || "16px",
        "font-family": this.styling.fontFamily || "sans-serif",
        cursor: this.isDisabled ? "not-allowed" : "auto",
        opacity: this.isDisabled ? 0.6 : 1,
      };
    },
    isCorrectAnswer() {
      return this.correctAnswers.includes(this.itemValue);
    },
    quizFeedbackType() {
      return this.isCorrectAnswer ? "success" : "danger";
    },
    quizFeedbackMessage() {
      if (this.isCorrectAnswer) {
        return this.item.correctAnswerFeedback || "Yup, Correct answer";
      }

      return this.item.wrongAnswerFeedback || "Opps! Wrong";
    },
  },
  methods: {
    chooseOption(event) {
      if (this.styling.multiPage) {
        if (event.keyCode == 38 || event.keyCode == 40) {
          this.arrowPressed = true;
        }
      }
    },
    dropdownSelect(event) {
      if (this.isEditingDisabled) return;

      if (!this.editing) {
        this.revealAnswer = true;
      }

      if (!this.arrowPressed) {
        this.autoProgressToNext();
      }
    },
  },
};
</script>

<style scoped>
.formester-input.formester-input__dropdown {
  min-height: 42px;
  padding-top: 0;
  padding-bottom: 0;
}
.formester-input__dropdown .dropdown-option {
  font-size: 18px;
}

.select-arrow {
  position: relative;
}
.select-arrow i {
  position: absolute;
  right: 14px;
  top: 14px;
  color: var(--answer-input-color);
}
</style>
